import type { ExerciseDefinition } from './types.ts';
import { GRADE_CODE_LABEL, noteIdentification, NOTE_IDENTIFICATION } from './note-identification/index.ts';
import { RHYTHM_GRADE_CODE_LABEL } from '../rhythm/grade.ts';
import { rhythmTapping, RHYTHM } from '../rhythm/exercise.ts';

/**
 * Registry of fully implemented exercise families. Only real, working families
 * belong here; curriculum and teacher tools list exactly these types.
 */
export const EXERCISE_TYPES = [NOTE_IDENTIFICATION, RHYTHM] as const;
export type ExerciseType = (typeof EXERCISE_TYPES)[number];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyExercise = ExerciseDefinition<any, any, any, any, string>;

export const EXERCISE_REGISTRY: Readonly<Record<ExerciseType, AnyExercise>> = {
  [NOTE_IDENTIFICATION]: noteIdentification,
  [RHYTHM]: rhythmTapping,
};

export function isExerciseType(value: string): value is ExerciseType {
  return (EXERCISE_TYPES as readonly string[]).includes(value);
}

export function getExercise(type: string): AnyExercise {
  if (!isExerciseType(type)) throw new Error(`Unknown exercise type: ${type}`);
  return EXERCISE_REGISTRY[type];
}

/** Engine version of every family, persisted with published assignments and sessions. */
export function exerciseEngineVersions(): Record<ExerciseType, number> {
  return Object.fromEntries(EXERCISE_TYPES.map((t) => [t, EXERCISE_REGISTRY[t].engineVersion])) as Record<ExerciseType, number>;
}

/** One-line description of a stored configuration, or null when it is not (yet) valid. */
export function describeExerciseConfig(type: string, config: unknown): string | null {
  if (!isExerciseType(type)) return null;
  const exercise = EXERCISE_REGISTRY[type];
  const result = exercise.validateConfig(config);
  return result.ok ? exercise.describeConfig(result.value) : null;
}

/**
 * Display label for any family's answer classification (error patterns).
 * Looked up on call, not at module load: the domain index has import cycles,
 * so the label tables may not be initialized yet when this module evaluates.
 */
export function gradeCodeLabel(code: string): string {
  return (GRADE_CODE_LABEL as Record<string, string>)[code] ?? (RHYTHM_GRADE_CODE_LABEL as Record<string, string>)[code] ?? code;
}
